package one.healthium.log_food.ui.scan_results

import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.user.UserInteractor
import one.healthium.profile.ui.food.models.AmountUiModel
import one.healthium.profile.ui.food.models.FloatPart
import one.healthium.profile.ui.food.models.FoodUiModel
import one.healthium.profile.ui.food.models.FoodUiOptions
import one.healthium.profile.ui.food.models.Units
import one.healthium.profile.ui.food.models.toOptions
import one.healthium.ui.common.event_producer.EventProducer
import one.healthium.ui.common.message.UiMessage
import one.healthium.ui.common.message.UiMessageManager
import one.healthium.ui.common.vm.BaseViewModel
import kotlin.time.Instant

class ScanResultsViewModel(
    private val userInteractor: UserInteractor,
    private val appPreferences: AppPreferences,
) : BaseViewModel() {

    private val _state = MutableStateFlow(ScanResultsUiState())
    val state = _state.asStateFlow()
    val uiMessage = UiMessageManager()
    val events = EventProducer<ScanResultsEvent>()

    fun init(foods: List<FoodUiModel>) {
        if (_state.value.foodItems.isNotEmpty()) return

        appPreferences.isMetricFlow
            .onEach { isMetric ->
                _state.update { current ->
                    val items = foods.map { food -> createFoodItem(food, isMetric) }
                    current.copy(
                        foodItems = items,
                        selectedAmountIndex = if (items.size == 1) 0 else null,
                    )
                }
            }
            .launch()
    }

    fun onAmountClicked(index: Int) {
        _state.update { it.copy(selectedAmountIndex = index, showDatePicker = false) }
    }

    fun onAmountChanged(amount: AmountUiModel) {
        _state.update { state ->
            val index = state.selectedAmountIndex ?: return@update state
            val items = state.foodItems.toMutableList()
            items[index] = items[index].copy(amount = amount)
            state.copy(foodItems = items)
        }
    }

    fun onLogTimeClicked() {
        _state.update { it.copy(showDatePicker = true, selectedAmountIndex = null) }
    }

    fun onTimeChanged(time: Instant) {
        _state.update { it.copy(selectedDate = time) }
    }

    fun onDismissPicker() {
        _state.update { it.copy(selectedAmountIndex = null, showDatePicker = false) }
    }

    fun onDeleteItem(index: Int) {
        _state.update { state ->
            val items = state.foodItems.toMutableList()
            if (index in items.indices) {
                items.removeAt(index)
            }
            val newSelectedAmountIndex = when {
                state.selectedAmountIndex == null -> null
                state.selectedAmountIndex == index -> null
                state.selectedAmountIndex > index -> state.selectedAmountIndex - 1
                else -> state.selectedAmountIndex
            }
            state.copy(
                foodItems = items,
                selectedAmountIndex = newSelectedAmountIndex,
            )
        }
    }

    fun onAddAllClicked(options: List<FoodUiOptions>) {
        launch {
            _state.update { it.copy(isLoading = true) }

            try {
                for (option in options) {
                    userInteractor.addFood(option.toOptions(), null)
                }
                _state.update { it.copy(isLoading = false) }
                events.trigger(ScanResultsEvent.FoodAdded)
            } catch (e: Exception) {
                Logger.e(e) { "Failed to add scanned foods" }
                _state.update { it.copy(isLoading = false) }
                uiMessage.add(UiMessage.throwable(e))
            }
        }
    }

    private fun createFoodItem(food: FoodUiModel, isMetric: Boolean): ScanResultFoodItem {
        val pieceCount = food.pieceCount
        val availableUnits = AmountUiModel.availableUnits(
            isMetric = isMetric,
            tablespoon = food.tablespoon,
            cups = food.cups,
            pieces = food.pieces,
            portion = food.portion,
            isLiquid = food.isLiquid,
        )
        val portionUnit = availableUnits.find { it is Units.Portion }
        val pieceUnit = availableUnits.find { it is Units.Piece }
        val defaultUnit = when {
            pieceCount != null && pieceCount > 0 && pieceUnit != null -> pieceUnit
            portionUnit != null -> portionUnit
            else -> AmountUiModel.defaultUnit(isMetric = isMetric, isLiquid = food.isLiquid)
        }

        val portionSize = food.portionSize
        val initialValue: Float = when {
            defaultUnit is Units.Piece && pieceCount != null && pieceCount > 0 -> pieceCount.toFloat()
            portionSize != null && portionSize > 0 -> portionSize / defaultUnit.gramValue
            portionUnit != null -> 1f
            isMetric -> 10f
            else -> 1f
        }
        val amount = AmountUiModel(
            integerPart = initialValue.toInt(),
            floatingPart = FloatPart.valueOf(initialValue),
            unit = defaultUnit,
            units = availableUnits,
        )
        return ScanResultFoodItem(food = food, amount = amount)
    }
}
